import React from 'react';
import BrollyDatePicker from '../datepicker/datepicker';
import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import Button from '../../form-control/Button';
import { ChevronRightIcon } from '@heroicons/react/24/solid';

export interface SelectedDateRange {
  from: string;
  to: string;
}
interface Props {
  children?: React.ReactNode;
  onSubmit: (data: SelectedDateRange) => void;
  autoSubmit?: boolean;
  initialStartDate?: string;
  initialEndDate?: string;
  debounceMs?: number;
}

function DashboardDatePickers({ onSubmit, autoSubmit = false, initialStartDate, initialEndDate, debounceMs = 300 }: Props) {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isChecked, setisChecked] = useState<boolean>(false)

  // Debounced submit function to prevent excessive API calls
  const debouncedSubmit = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (startDate && endDate) {
            submit();
          }
        }, debounceMs);
      };
    })(),
    [startDate, endDate, debounceMs]
  );

  // Initialize dates from props and keep them in sync - combined for efficiency
  useEffect(() => {
    if (initialStartDate) {
      const date = moment(initialStartDate).toDate();
      setStartDate(date);
    } else {
      setStartDate(null);
    }
    
    if (initialEndDate) {
      const date = moment(initialEndDate).toDate();
      setEndDate(date);
    } else {
      setEndDate(null);
    }
  }, [initialStartDate, initialEndDate]);

  // Auto-submit when both dates are selected and autoSubmit is true
  useEffect(() => {
    if (autoSubmit && startDate && endDate) {
      debouncedSubmit();
    }
  }, [startDate, endDate, autoSubmit, debouncedSubmit]);

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
  };

  const submit = () => {
    const data = {
      from: moment(startDate).format('YYYY-MM-DD'),
      to: moment(endDate).format('YYYY-MM-DD')
    };

    onSubmit(data);
    setisChecked(true)
  };

  return (
    <div className={`w-fit bg-white dark:bg-darkBg rounded-lg datepickers flex items-center sm:mt-0`}>
      <div className="px-0">
        <BrollyDatePicker
          selectedDate={startDate}
          onChange={handleStartDateChange}
          placeholderText="Start Date"
        />
      </div>
      <div className="px-0 ">
        <BrollyDatePicker
          selectedDate={endDate}
          onChange={handleEndDateChange}
          placeholderText="End Date"
        />
      </div>
      {!autoSubmit && (
        <Button
          disabled={!startDate || !endDate}
          onClick={submit}
          bgColor='primary'
          className=" font-semibold py-2 px-4 sm:mt-0 disabled:bg-red lg:ml-3 lg:mt-3 xl:mt-0 border-none flex items-center"
        >
          Check
          <ChevronRightIcon className="ml-3 size-4 font-semibold" />
        </Button>
      )}
    </div>
  );
}

export default DashboardDatePickers;
